/**
 * HTTP client using ky — unified request interceptors, Cookie auth, CSRF, error handling.
 */
import ky from "ky";
import { toast } from "sonner";

import { DEMO_GENERATION_HEADER, demoGeneration } from "@/lib/demo-generation";
import { useAuthStore } from "@/stores/auth";

/** Refresh token promise — deduplicates concurrent 401 retries */
let refreshPromise: Promise<boolean> | null = null;

/**
 * ky waits out a retried 503's Retry-After / X-RateLimit-Reset headers, and the
 * API sends X-RateLimit-Reset (seconds left in its rate-limit window) on every
 * response — so a 503 such as an unconfigured server surfaced only after up to
 * two minutes of silent retries. Only 413 and 429 honor those headers now; a
 * 503 retries on ky's short backoff and fails within a second.
 */
export const API_RETRY = { afterStatusCodes: [413, 429] };

export function applyCurrentShopHeader(request: Request, currentShopId: string | null): void {
  if (currentShopId && !request.headers.has("X-Shop-Id")) {
    request.headers.set("X-Shop-Id", currentShopId);
  }
}

export function shouldClearCurrentShop(request: Request, currentShopId: string | null): boolean {
  const requestedShopId = request.headers.get("X-Shop-Id");
  return currentShopId !== null && (requestedShopId === null || requestedShopId === currentShopId);
}

async function tryRefreshToken(): Promise<boolean> {
  if (!refreshPromise) {
    refreshPromise = fetch("/api/v1/auth/refresh", {
      method: "POST",
      credentials: "include",
    })
      .then((res) => res.ok)
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

export const api = ky.create({
  prefixUrl: "/api/v1",
  credentials: "include",
  retry: API_RETRY,
  hooks: {
    beforeRequest: [
      (request) => {
        // CSRF protection: all write requests must carry X-Requested-With header
        if (["POST", "PUT", "PATCH", "DELETE"].includes(request.method)) {
          request.headers.set("X-Requested-With", "XMLHttpRequest");
          const generation = demoGeneration();
          if (generation && !new URL(request.url).pathname.startsWith("/api/v1/auth/")) {
            request.headers.set(DEMO_GENERATION_HEADER, generation);
          }
        }

        // Current-shop context: tell the backend which shop this request targets.
        // The backend validates it against the caller's Workspace membership and
        // Shop Scope; without it, it falls back to the user's first shop.
        const shopId = useAuthStore.getState().currentShopId;
        applyCurrentShopHeader(request, shopId);
      },
    ],
    afterResponse: [
      async (request, options, response) => {
        if (response.status === 401) {
          // Try to refresh the token. Treat a network error during refresh as
          // a failed refresh so we still redirect instead of rejecting here
          // (which would leave the calling query with an unhandled throw).
          let refreshed = false;
          try {
            refreshed = await tryRefreshToken();
          } catch {
            refreshed = false;
          }
          if (refreshed) {
            // Retry the original request
            return ky(request);
          }
          // Refresh failed — hard-navigate to login. This covers in-page API
          // calls (TanStack Query) that 401 mid-session; the initial auth gate
          // in routes/_authed/route.tsx does its own router-level redirect.
          // Guard against looping when a call on /login itself 401s.
          if (
            options.context.suppressAuthRedirect !== true &&
            !window.location.pathname.startsWith("/login")
          ) {
            window.location.href = "/login?reason=expired";
          }
        }

        if (response.status === 409) {
          const body = await response
            .clone()
            .json()
            .catch(() => null);
          if (
            (body as { error?: { code?: string } } | null)?.error?.code ===
            "demo.generation_changed"
          ) {
            // ADP-271: the demo data was restored after this page loaded.
            const { i18n } = await import("@/i18n");
            toast.error(i18n.t("errors:demo.generation_changed"));
            setTimeout(() => window.location.reload(), 1500);
          }
        }

        if (response.status === 403) {
          // The persisted currentShopId may be stale (membership revoked).
          // Clear it so the next request falls back to the user's first shop,
          // instead of every request 403ing with no way out of the loop.
          const body = await response
            .clone()
            .json()
            .catch(() => null);
          if (
            (body as { error?: { code?: string } } | null)?.error?.code === "shop.forbidden" &&
            shouldClearCurrentShop(request, useAuthStore.getState().currentShopId)
          ) {
            useAuthStore.getState().clearCurrentShop();
          }
          // Lazy import: keeps the i18n runtime out of api.ts's module graph
          // (unit tests mock react-i18next partially). Prefer the response's
          // own error code when the code table covers it.
          const { i18n } = await import("@/i18n");
          const code = (body as { error?: { code?: string } } | null)?.error?.code;
          const key =
            code && i18n.exists(`errors:${code}`) ? `errors:${code}` : "errors:auth.forbidden";
          toast.error(i18n.t(key));
        }
      },
    ],
  },
});
